import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Typography,
  Box,
  Grid,
  Chip,
  IconButton,
  Divider,
  Card,
  CardContent,
  CardHeader,
  Stack,
  Paper,
  Tooltip
} from '@mui/material';
import { Icon } from '@iconify/react';
import { useTheme } from '@mui/material';

// import status color map
import { statusColorMap, Status } from '../constants/ocpi.constant';
import { formatOcpiCurrency, formatOcpiDate } from 'src/utils/formatTime';

const LocationDetails = ({ open, onClose, location }) => {
  const theme = useTheme();

  if (!location) return null;

  // Power type icon mapping
  const getPowerTypeIcon = (powerType) => {
    switch (powerType?.toLowerCase()) {
      case 'ac_1_phase':
      case 'ac_3_phase':
        return 'eva:flash-fill';
      case 'dc':
        return 'eva:battery-fill';
      default:
        return 'eva:flash-fill';
    }
  };

  // Format power values
  const formatPower = (power) => {
    if (power >= 1000) {
      return `${(power / 1000).toFixed(0)} kW`;
    }
    return `${power} W`;
  };

  const locationStatus = location.availableEvseCount > 0 ? Status.ACTIVE : Status.INACTIVE;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          maxHeight: '95vh'
        }
      }}
    >
      {/* Header */}
      <DialogTitle
        sx={{
          p: 3,
          pb: 2,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderBottom: `1px solid ${theme.palette.divider}`
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Icon
            icon="eva:map-pin-fill"
            style={{ fontSize: '24px', color: theme.palette.primary.main }}
          />
          <Typography
            variant="h5"
            sx={{
              color: theme.palette.primary.main,
              fontWeight: 600
            }}
          >
            Location Details
          </Typography>
        </Box>
        <IconButton
          onClick={onClose}
          sx={{
            color: theme.palette.text.secondary,
            '&:hover': {
              backgroundColor: theme.palette.action.hover
            }
          }}
        >
          <Icon icon="eva:close-fill" fontSize={20} />
        </IconButton>
      </DialogTitle>

      {/* Content */}
      <DialogContent sx={{ p: 3 }}>
        <Grid container spacing={4}>
          {/* Basic Information */}
          <Grid item xs={12}>
            <Typography
              variant="h6"
              sx={{
                mb: 3,
                color: theme.palette.text.primary,
                fontWeight: 600
              }}
            >
              Basic Information
            </Typography>

            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <Stack spacing={2}>
                  <Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                      Location Name
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {location.name}
                    </Typography>
                  </Box>

                  <Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                      Location ID
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {location.id}
                    </Typography>
                  </Box>
                  <Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Status
                  </Typography>
                  <Chip
                    label={locationStatus}
                    color={statusColorMap[locationStatus].color}
                    size="medium"
                    sx={{
                      fontWeight: 600,
                      textTransform: 'capitalize',
                      bgcolor: statusColorMap[locationStatus].bgcolor,
                      color: statusColorMap[locationStatus].text,
                    }}
                  />
                </Box>
                </Stack>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Stack spacing={2}>
                  <Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                      Party ID
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {location.partyId}
                    </Typography>
                  </Box>

                  <Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                      Country Code
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {location.countryCode}
                    </Typography>
                  </Box>
                </Stack>
              </Grid>
            </Grid>
          </Grid>

          {/* Address Information */}
          {location.address && (
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography
                variant="h6"
                sx={{
                  mb: 3,
                  color: theme.palette.text.primary,
                  fontWeight: 600
                }}
              >
                Address Information
              </Typography>

              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <Stack spacing={2}>
                    <Box>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                        Street
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {location.address}
                      </Typography>
                    </Box>

                    <Box>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                        City
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {location.city}
                      </Typography>
                    </Box>

                    <Box>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                        Coordinates
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {location.coordinates.latitude}, {location.coordinates.longitude}
                      </Typography>
                    </Box>
                  </Stack>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Stack spacing={2}>
                    <Box>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                        Postal Code
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {location.postalCode}
                      </Typography>
                    </Box>

                    <Box>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                        Country
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {location.country}
                      </Typography>
                    </Box>
                  </Stack>
                </Grid>
              </Grid>
            </Grid>
          )}

          {/* EVSE Details */}
          {location.evses && location.evses.length > 0 && (
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography
                variant="h6"
                sx={{
                  mb: 3,
                  color: theme.palette.text.primary,
                  fontWeight: 600,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1
                }}
              >
                <Icon icon="eva:charging-fill" style={{ fontSize: '20px' }} />
                EVSEs ({location.evses.length})
              </Typography>

              <Grid container spacing={3}>
                {location.evses.map((evse, index) => (
                  <Grid item xs={12} md={6} key={evse.uid || index}>
                    <Card
                      sx={{
                        height: '100%',
                        border: `1px solid ${theme.palette.divider}`,
                        borderRadius: 2,
                        boxShadow: 'none'
                      }}
                    >
                      <CardHeader
                        title={
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Typography
                              variant="h6"
                              sx={{
                                fontWeight: 600,
                                color: theme.palette.text.primary
                              }}
                            >
                              {evse.evseUid}
                            </Typography>
                            <Chip
                              label={evse.status}
                              color={statusColorMap[evse.status].color}
                              size="small"
                              sx={{
                                fontWeight: 600,
                                bgcolor: statusColorMap[evse.status].bgcolor,
                                color: statusColorMap[evse.status].text,
                              }}
                            />
                          </Box>
                        }
                        subheader={
                          <Typography variant="body2" color="text.secondary">
                            ID: {evse.evseUid} • Connectors: {location.connectors?.length || 0}
                          </Typography>
                        }
                        sx={{ pb: 1 }}
                      />

                      <CardContent sx={{ pt: 0 }}>
                        {/* Connectors */}
                        {location.connectors && location.connectors.length > 0 && (
                          <Box>
                            <Typography
                              variant="subtitle1"
                              sx={{
                                mb: 2,
                                color: theme.palette.text.primary,
                                fontWeight: 600
                              }}
                            >
                              Connectors
                            </Typography>

                            <Grid container spacing={2}>
                              {location.connectors.map((connector, connIndex) => (
                                <Grid item xs={12} sm={6} key={connector.connectorId || connIndex}>
                                  <Paper
                                    elevation={0}
                                    sx={{
                                      p: 2,
                                      borderRadius: 1.5,
                                      backgroundColor: theme.palette.grey[50]
                                    }}
                                  >
                                    {/* Connector Header */}
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                      <Typography
                                        variant="subtitle2"
                                        sx={{ fontWeight: 600 }}
                                      >
                                        #{connector.connectorId}
                                      </Typography>
                                      <Chip
                                        label={connector.standard || 'Unknown'}
                                        size="small"
                                        sx={{
                                          fontSize: '0.7rem',
                                          fontWeight: 500
                                        }}
                                      />
                                    </Box>

                                    {/* Power Information */}
                                    <Stack spacing={1} sx={{ mb: 2 }}>
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Icon
                                          icon={getPowerTypeIcon(connector.powerType)}
                                          style={{ fontSize: '16px', color: theme.palette.warning.main }}
                                        />
                                        <Typography
                                          variant="body2"
                                          sx={{ fontWeight: 600 }}
                                        >
                                          {connector.powerType || 'AC_3_PHASE'}
                                        </Typography>
                                      </Box>

                                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <Typography variant="body2" color="text.secondary">
                                          Max Power:
                                        </Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                          {formatPower(connector.maxPower || 0)}
                                        </Typography>
                                      </Box>

                                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <Typography variant="body2" color="text.secondary">
                                          Max Voltage:
                                        </Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                          {connector.maxVoltage || 400}V
                                        </Typography>
                                      </Box>

                                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <Typography variant="body2" color="text.secondary">
                                          Max Amperage:
                                        </Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                          {connector.maxAmperage || 32}A
                                        </Typography>
                                      </Box>
                                    </Stack>

                                    {/* Divider */}
                                    <Divider sx={{ my: 2 }} />

                                    {/* Tariff Information */}
                                    <Box>
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                        <Icon
                                          icon="eva:dollar-sign-fill"
                                          style={{ fontSize: '14px', color: theme.palette.success.main }}
                                        />
                                        <Typography
                                          variant="subtitle2"
                                          sx={{
                                            fontWeight: 600,
                                            fontSize: '0.8rem'
                                          }}
                                        >
                                          Tariffs:
                                        </Typography>
                                      </Box>

                                      <Paper
                                        elevation={0}
                                        sx={{
                                          p: 1.5,
                                          backgroundColor: 'transparent',
                                          border: `1px solid ${theme.palette.grey[300]}`,
                                          borderRadius: 1,
                                          boxShadow: 'none'
                                        }}
                                      >
                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: "space-between", gap: 1, mb: 1 }}>
                                          <Tooltip title={location.tariffs[0].tariffId || 'TARIFF_001'}>
                                            <Typography
                                              variant="body2"
                                              sx={{ 
                                                fontWeight: 600,
                                                maxWidth: '120px',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap'
                                              }}
                                            >
                                              {location.tariffs[0].tariffId || 'TARIFF_001'}
                                            </Typography>
                                          </Tooltip>
                                          <Chip
                                            label={connector.tariffProfile || 'REGULAR'}
                                            size="small"
                                            sx={{
                                              fontSize: '0.65rem',
                                              height: '20px',
                                              fontWeight: 500
                                            }}
                                          />
                                        </Box>

                                        <Stack spacing={0.5}>
                                          {location.tariffs[0].elements[0].price_components.map((priceComponent, index) => (
                                            <Box key={index} sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                              <Typography variant="caption" color="text.secondary">
                                                {priceComponent.type}:
                                              </Typography>
                                              <Typography variant="caption" sx={{ fontWeight: 600 }}>
                                                {formatOcpiCurrency(priceComponent.price)}
                                              </Typography>
                                            </Box>
                                          ))}
                                        </Stack>
                                      </Paper>
                                    </Box>
                                  </Paper>
                                </Grid>
                              ))}
                            </Grid>
                          </Box>
                        )}
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Grid>
          )}

          {/* Last Updated */}
          <Grid item xs={12}>
            <Divider sx={{ my: 2 }} />
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                Last Updated
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                {formatOcpiDate(location.lastUpdated)}
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </DialogContent>
    </Dialog>
  );
};

export default LocationDetails;
